// originally written by @imoaazahmed

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useBrand } from "@webuild-ai/react-auth";

const ThemeProps = {
  key: "theme",
  light: "light",
  dark: "dark",
} as const;

type Theme = typeof ThemeProps.light | typeof ThemeProps.dark;

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  isLight: boolean;
  isReady: boolean;
  setLightTheme: () => void;
  setDarkTheme: () => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { brand } = useBrand();

  // Apply the brand colors to the document
  const configureBrandTheme = useCallback((theme: Theme, currentBrand: typeof brand) => {
    const isDark = theme === ThemeProps.dark;
    const brandColors = isDark ? currentBrand.colors.dark : currentBrand.colors.light;

    document.documentElement.classList.remove(ThemeProps.light, ThemeProps.dark);
    document.documentElement.classList.add(theme);
    document.documentElement.style.setProperty("--brand-primary", brandColors.primary);
    document.documentElement.style.setProperty("--brand-secondary", brandColors.secondary);
    brandColors.tertiary &&
      document.documentElement.style.setProperty("--brand-tertiary", brandColors.tertiary);
    document.documentElement.style.setProperty("--current-color", brandColors.primary);

    // Set brand-specific dark mode background if defined
    if (isDark && currentBrand.colors.dark.background) {
      document.documentElement.style.setProperty(
        "--brand-dark-bg-gradient",
        currentBrand.colors.dark.background,
      );
    } else if (isDark) {
      // Reset to default dark background for brands without custom background
      document.documentElement.style.setProperty(
        "--brand-dark-bg-gradient",
        "linear-gradient(180deg, #0d0d1a 0%, #1a1a3a 50%, #151528 100%)",
      );
    }
    currentBrand.fontFamily &&
      document.documentElement.style.setProperty("--default-font-family", currentBrand.fontFamily);

    // if (brandColors.borderColor) {
    //   document.documentElement.style.setProperty("--heroui-divider", brandColors.borderColor);
    // }
  }, []);

  // Initialize theme from localStorage without applying brand colours yet
  // Brand colours will be applied in the useEffect once brand is ready
  const [theme, setTheme] = useState<Theme>(() => {
    const storedTheme = localStorage.getItem(ThemeProps.key) as Theme | null;
    const initialTheme = storedTheme || ThemeProps.light;

    // Only set the theme class initially, don't apply brand colours yet
    document.documentElement.classList.remove(ThemeProps.light, ThemeProps.dark);
    document.documentElement.classList.add(initialTheme);

    return initialTheme;
  });

  // Track if brand colours have been applied to prevent flash
  const [isReady, setIsReady] = useState(false);

  const isDark = useMemo(() => {
    return theme === ThemeProps.dark;
  }, [theme]);

  const isLight = useMemo(() => {
    return theme === ThemeProps.light;
  }, [theme]);

  const _setTheme = useCallback(
    (newTheme: Theme) => {
      localStorage.setItem(ThemeProps.key, newTheme);
      configureBrandTheme(newTheme, brand);
      setTheme(newTheme);
    },
    [configureBrandTheme, brand],
  );

  const setLightTheme = useCallback(() => _setTheme(ThemeProps.light), [_setTheme]);

  const setDarkTheme = useCallback(() => _setTheme(ThemeProps.dark), [_setTheme]);

  const toggleTheme = useCallback(
    () => (theme === ThemeProps.dark ? setLightTheme() : setDarkTheme()),
    [theme, setLightTheme, setDarkTheme],
  );

  // Apply brand colours once brand is ready and when theme changes
  useEffect(() => {
    configureBrandTheme(theme, brand);
    setIsReady(true);
  }, [brand, theme, configureBrandTheme]);

  const value = useMemo(
    () => ({
      theme,
      isDark,
      isLight,
      isReady,
      setLightTheme,
      setDarkTheme,
      toggleTheme,
    }),
    [theme, isDark, isLight, isReady, setLightTheme, setDarkTheme, toggleTheme],
  );

  // Don't render children until theme and brand colours are ready
  // This prevents the flash of incorrect colours
  if (!isReady) {
    return null;
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
